import React, { memo } from 'react';
import { Position } from 'reactflow';
import { Tooltip } from 'antd';
import { BranchesOutlined } from '@ant-design/icons';
import EnhancedHandle from './EnhancedHandle';

const ConditionalNode = ({ data, isConnectable, selected }) => {
    // Determine condition type and description
    const getConditionInfo = () => {
        const type = data.conditionType || data.config?.type;
        const condition = data.config?.condition || '';
        const field = data.config?.field || '';
        const operator = data.config?.operator || '';
        const value = data.config?.value || '';
        const prompt = data.config?.prompt || '';

        let displayText = '';
        let description = '';

        switch (type) {
            case 'javascript':
                displayText = condition.length > 40 ? condition.substring(0, 37) + '...' : condition;
                description = `JavaScript: ${condition}`;
                break;
            case 'field':
                displayText = `${field} ${operator} ${value}`;
                description = `Check if ${field} ${operator} ${value}`;
                break;
            case 'ai':
                displayText = prompt.length > 40 ? prompt.substring(0, 37) + '...' : prompt;
                description = `AI Evaluation: ${prompt}`;
                break;
            default:
                displayText = 'Condition not set';
                description = 'Condition not configured';
        }

        return { type, displayText, description };
    };

    const conditionInfo = getConditionInfo();

    // Show execution count badge if available
    const ExecutionCountBadge = () => {
        if (data.executionCount === undefined) return null;

        return (
            <div style={{
                position: 'absolute',
                top: -8,
                right: -8,
                background: '#1890ff',
                color: 'white',
                borderRadius: '50%',
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 10
            }}>
                {data.executionCount}
            </div>
        );
    };

    return (
        <Tooltip title={conditionInfo.description}>
            <div
                style={{
                    width: 220,
                    position: 'relative',
                    background: selected ? '#f0f5ff' : 'white',
                    border: selected ? '1px solid #1890ff' : '1px solid #d9d9d9',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    fontFamily: 'Arial, sans-serif'
                }}
            >
                <ExecutionCountBadge />

                {/* Header */}
                <div style={{
                    background: '#1890ff',
                    color: 'white',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid #d9d9d9'
                }}>
                    <BranchesOutlined style={{ fontSize: 16, marginRight: 8 }} />
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {data.label || 'Conditional'}
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '12px 16px' }}>
                    <div style={{
                        fontSize: '13px',
                        fontFamily: conditionInfo.type === 'javascript' ? 'monospace' : 'inherit'
                    }}>
                        {conditionInfo.displayText}
                    </div>
                </div>

                {/* Branch Indicators */}
                <div style={{
                    display: 'flex',
                    borderTop: '1px solid #e8e8e8',
                    justifyContent: 'space-between'
                }}>
                    <div style={{
                        background: 'rgba(34, 197, 94, 0.1)',
                        padding: '5px 0',
                        width: '50%',
                        textAlign: 'center',
                        color: '#22c55e',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        borderRight: '1px solid #e8e8e8'
                    }}>
                        True
                    </div>
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        padding: '5px 0',
                        width: '50%',
                        textAlign: 'center',
                        color: '#ef4444',
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}>
                        False
                    </div>
                </div>

                {/* Input handle - enhanced */}
                <EnhancedHandle
                    type="target"
                    position={Position.Top}
                    id="in"
                    style={{
                        top: -6
                    }}
                    color="#1890ff"
                    isConnectable={isConnectable}
                />

                {/* True output handle - enhanced */}
                <EnhancedHandle
                    type="source"
                    position={Position.Bottom}
                    id="true"
                    style={{
                        left: '25%',
                        bottom: -6
                    }}
                    color="#22c55e"
                    isConnectable={isConnectable}
                />

                {/* False output handle - enhanced */}
                <EnhancedHandle
                    type="source"
                    position={Position.Bottom}
                    id="false"
                    style={{
                        left: '75%',
                        bottom: -6
                    }}
                    color="#ef4444"
                    isConnectable={isConnectable}
                />
            </div>
        </Tooltip>
    );
};

export default memo(ConditionalNode);
